import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Briefcase, ArrowLeft, CheckCircle2, Zap, Building2, Loader2, CreditCard, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOBSEEKER_PLANS = [
  { id: 'free', name: 'Free', price: 0, period: '', features: ['Browse & search all jobs — unlimited', 'View full job details', '10 applications/month', 'Basic community access', 'Job alerts'] },
  { id: 'plus', name: 'Plus', price: 4.99, period: '/mo', features: ['Everything in Free', 'Unlimited applications', 'Quick Apply — one-click apply', 'Save & track favorite jobs', 'Full community access', 'Resume storage', 'Priority job alerts'] },
  { id: 'pro', name: 'Pro', price: 12.99, period: '/mo', highlight: true, features: ['Everything in Plus', 'AI Resume Builder (GPT/Claude/Gemini)', 'Priority visibility to employers', 'Assessment prep tools', 'Career coaching resources', 'Advanced analytics'] },
];

const COMPANY_PLANS = [
  { id: 'starter', name: 'Starter', price: 99, period: '/mo', features: ['Up to 3 job posts', 'Basic applicant view', 'Company profile', 'Email support', 'Live Hiring Events (Chat)'] },
  { id: 'growth', name: 'Growth', price: 249, period: '/mo', highlight: true, features: ['Up to 15 job posts', 'Featured listings', 'Applicant filtering', 'Analytics dashboard', 'Priority support', 'Live Hiring Events (Chat + Video/Zoom)', 'Waiting room & candidate queue'] },
  { id: 'enterprise', name: 'Enterprise', price: 499, period: '/mo', features: ['Unlimited job posts', 'Premium branding', 'Advanced analytics', 'Dedicated account manager', 'API access', 'Live Hiring Events (Chat + Video/Zoom)', 'Featured Employer Badge included', 'Priority candidate matching'] },
];

export default function Membership() {
  const { user, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const isBusiness = user?.role === 'business';

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) pollPaymentStatus(sessionId);
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) { setPaymentStatus('timeout'); return; }
    setChecking(true);
    try {
      const { data } = await axios.get(`${API}/checkout/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') { setPaymentStatus('success'); await checkAuth(); setChecking(false); return; }
      if (data.status === 'expired') { setPaymentStatus('expired'); setChecking(false); return; }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) { setPaymentStatus('error'); setChecking(false); }
  };

  const handleCheckout = async (planId) => {
    setLoading(planId);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/checkout/session`, { plan_id: planId, origin_url: origin }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) { alert(err.response?.data?.detail || 'Failed to start checkout'); }
    setLoading('');
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will be downgraded to the Free plan immediately.')) return;
    setCancelling(true);
    try { await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true }); setPaymentStatus('cancelled'); await checkAuth(); } catch (err) { alert(err.response?.data?.detail || 'Failed to cancel subscription'); }
    setCancelling(false);
  };

  const plans = isBusiness ? COMPANY_PLANS : JOBSEEKER_PLANS;
  const accentColor = isBusiness ? '#D97757' : '#047857';
  const accentHover = isBusiness ? '#C4623F' : '#065F46';

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>

        {/* Payment Status Banners */}
        {paymentStatus === 'success' && (
          <div className="bg-[#A7F3D0]/30 border border-[#047857]/20 rounded-2xl p-6 mb-8 flex items-center gap-4" data-testid="payment-success">
            <CheckCircle2 size={24} className="text-[#047857]" strokeWidth={1.5} />
            <div>
              <p className="font-medium text-[#047857]" style={{fontFamily:'Outfit'}}>Payment Successful!</p>
              <p className="text-sm text-[#1C2A24]/60">Your membership has been upgraded. Enjoy your new features!</p>
            </div>
          </div>
        )}
        {paymentStatus === 'cancelled' && (
          <div className="bg-[#F4F1E8] border border-[#E5E0D8] rounded-2xl p-6 mb-8 flex items-center gap-4" data-testid="payment-cancelled">
            <CheckCircle2 size={24} className="text-[#1C2A24]/60" strokeWidth={1.5} />
            <div>
              <p className="font-medium text-[#1C2A24]" style={{fontFamily:'Outfit'}}>Subscription Cancelled</p>
              <p className="text-sm text-[#1C2A24]/60">You've been moved to the Free plan. You can re-subscribe anytime.</p>
            </div>
          </div>
        )}
        {checking && (
          <div className="bg-[#F4F1E8] border border-[#E5E0D8] rounded-2xl p-6 mb-8 flex items-center gap-4" data-testid="payment-checking">
            <Loader2 size={24} className="text-[#047857] animate-spin" strokeWidth={1.5} />
            <p className="text-[#1C2A24]/60">Verifying your payment...</p>
          </div>
        )}

        {/* Context-aware header */}
        <div className="text-center mb-10" data-testid="pricing-header">
          <div className="inline-flex items-center gap-2 mb-4">
            {isBusiness
              ? <div className="w-10 h-10 rounded-xl bg-[#D97757]/10 flex items-center justify-center"><Building2 size={20} className="text-[#D97757]" strokeWidth={1.5} /></div>
              : <div className="w-10 h-10 rounded-xl bg-[#047857]/10 flex items-center justify-center"><Zap size={20} className="text-[#047857]" strokeWidth={1.5} /></div>
            }
          </div>
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium" style={{fontFamily:'Outfit'}} data-testid="pricing-heading">
            {isBusiness ? 'Business Plans' : 'Upgrade Your Job Search'}
          </h1>
          <p className="text-[#1C2A24]/60 mt-2" data-testid="pricing-subheading">
            {isBusiness
              ? 'Post jobs, manage applicants, and host live hiring events'
              : 'Unlock unlimited applications, Quick Apply, and AI resume tools'}
          </p>
          {user && <p className="text-sm mt-2" style={{color: accentColor}}>Current plan: <span className="font-semibold capitalize">{user.membership || 'free'}</span></p>}
        </div>

        {/* Business context banner */}
        {isBusiness && (
          <div className="bg-[#D97757]/10 rounded-2xl p-4 mb-6 border border-[#D97757]/20 flex items-center gap-3" data-testid="business-context-banner">
            <TrendingUp size={18} className="text-[#D97757] shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-[#1C2A24]/80">Subscribe to start posting WFH job listings, hosting hiring events, and reviewing applicants. LLCs, startups, and major companies welcome.</p>
          </div>
        )}

        {/* Plans grid — role-specific, no tabs */}
        <div className="grid md:grid-cols-3 gap-6" data-testid="plans-grid">
          {plans.map(plan => {
            const isCurrent = user?.membership === plan.id;
            const isFree = plan.price === 0;
            return (
              <div key={plan.id} className={`bg-white rounded-3xl border p-8 ${plan.highlight ? 'pricing-highlight' : 'border-[#E5E0D8]'} shadow-[0_8px_30px_rgb(0,0,0,0.04)]`} data-testid={`plan-${plan.id}`}>
                {plan.highlight && <div className="text-xs font-medium rounded-full px-3 py-1 inline-block mb-4" style={{backgroundColor: `${accentColor}15`, color: accentColor}}>{isBusiness ? 'Best Value' : 'Most Popular'}</div>}
                <h3 className="text-xl font-medium" style={{fontFamily:'Outfit'}}>{plan.name}</h3>
                <div className="mt-4 mb-6">
                  <span className="text-4xl font-semibold" style={{fontFamily:'Outfit'}}>${plan.price === 0 ? '0' : plan.price}</span>
                  <span className="text-sm text-[#1C2A24]/60">{plan.period}</span>
                </div>
                <ul className="space-y-3 text-sm mb-8">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#047857] mt-0.5 shrink-0" strokeWidth={1.5} />{f}</li>
                  ))}
                </ul>
                {isCurrent ? (
                  <Button disabled className="w-full rounded-full h-11 bg-[#F4F1E8] text-[#1C2A24]/50">Current Plan</Button>
                ) : isFree ? (
                  <Button disabled className="w-full rounded-full h-11 bg-[#F4F1E8] text-[#1C2A24]">Free Forever</Button>
                ) : (
                  <Button onClick={() => handleCheckout(plan.id)} disabled={!!loading}
                      className="w-full rounded-full h-11 text-white"
                      style={{backgroundColor: plan.highlight ? accentColor : '#F4F1E8', color: plan.highlight ? 'white' : '#1C2A24'}}
                      data-testid={`checkout-${plan.id}`}>
                      {loading === plan.id ? <Loader2 size={16} className="animate-spin" /> : <><CreditCard size={14} className="mr-2" /> Subscribe</>}
                    </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Cancel Subscription */}
        {user && user.membership && user.membership !== 'free' && (
          <div className="mt-12 border-t border-[#E5E0D8] pt-8" data-testid="cancel-section">
            <div className="bg-white rounded-2xl border border-[#E5E0D8] p-6 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#1C2A24]" style={{fontFamily:'Outfit'}}>Cancel Subscription</p>
                <p className="text-xs text-[#1C2A24]/50 mt-1">You're currently on the <span className="capitalize font-medium">{user.membership}</span> plan. Cancelling will downgrade you to Free immediately.</p>
              </div>
              <Button onClick={handleCancel} disabled={cancelling} variant="outline"
                className="rounded-full border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 h-10 px-6 text-sm shrink-0" data-testid="cancel-subscription-btn">
                {cancelling ? <Loader2 size={14} className="animate-spin" /> : 'Cancel Subscription'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
